<template>
  <el-dialog
    v-model="visible"
    :title="t('header.settings')"
    width="700"
    class="settings-dialog"
  >
    <div class="settings-shell">
      <!-- Left nav: one entry per settings category. -->
      <el-menu
        class="settings-nav"
        :default-active="activeTab"
        @select="handleTabSelect"
      >
        <el-menu-item index="appearance">
          <el-icon><Brush /></el-icon>
          <span>{{ t('header.sectionAppearance') }}</span>
        </el-menu-item>
        <el-menu-item index="account">
          <el-icon><User /></el-icon>
          <span>{{ t('header.sectionAccount') }}</span>
        </el-menu-item>
        <el-menu-item v-if="user.userType === 'SYS'" index="privacy">
          <el-icon><Lock /></el-icon>
          <span>{{ t('header.sectionPrivacy') }}</span>
        </el-menu-item>
      </el-menu>

      <!-- Right content: one panel per category (v-show keeps form state alive). -->
      <div class="settings-content">
        <!-- ===== Appearance ===== -->
        <div v-show="activeTab === 'appearance'" class="settings-list">
          <div class="settings-row">
            <div class="settings-row__text">
              <div class="settings-row__title">{{ t('locale.switcher') }}</div>
              <div class="settings-row__desc">{{ t('header.languageDesc') }}</div>
            </div>
            <el-select
              :model-value="currentLocale"
              style="width: 180px"
              @change="handleLocaleChange"
            >
              <el-option
                v-for="code in SUPPORTED_LOCALES"
                :key="code"
                :value="code"
                :label="t(`locale.${code}`)"
              />
            </el-select>
          </div>
          <!-- Font theme picker temporarily hidden — the three current
               presets look too similar in practice. The setFontTheme /
               applyFontTheme plumbing in store/common.ts is intentionally
               left intact so we can re-enable the picker (with more
               distinct options) by flipping v-if back to true. Default
               remains "modern" via DEFAULT_FONT_THEME. -->
          <div v-if="false" class="settings-row settings-row--stacked">
            <div class="settings-row__text">
              <div class="settings-row__title">{{ t('header.fontTheme') }}</div>
              <div class="settings-row__desc">{{ t('header.fontThemeDesc') }}</div>
            </div>
            <div class="font-theme-picker">
              <button
                v-for="theme in fontThemeOptions"
                :key="theme.value"
                type="button"
                class="font-theme-card"
                :class="[
                  `font-theme-card--${theme.value}`,
                  { 'is-active': commomStore.fontTheme === theme.value },
                ]"
                @click="commomStore.setFontTheme(theme.value)"
              >
                <div class="font-theme-card__sample-en">Aa Clinical 2026</div>
                <div class="font-theme-card__sample-zh">中文医疗数据</div>
                <div class="font-theme-card__label">{{ t(theme.labelKey) }}</div>
              </button>
            </div>
          </div>
          <div class="settings-row">
            <div class="settings-row__text">
              <div class="settings-row__title">
                {{ t('header.compactMode') }}<span class="settings-row__beta">{{ t('header.betaSuffix') }}</span>
              </div>
              <div class="settings-row__desc">{{ t('header.compactModeDesc') }}</div>
            </div>
            <el-switch
              :model-value="commomStore.compactForm"
              @change="commomStore.toggleCompactForm()"
            />
          </div>
          <div class="settings-row">
            <div class="settings-row__text">
              <div class="settings-row__title">
                {{ t('header.darkMode') }}<span class="settings-row__beta">{{ t('header.betaSuffix') }}</span>
              </div>
              <div class="settings-row__desc">{{ t('header.darkModeDesc') }}</div>
            </div>
            <el-switch
              :model-value="commomStore.darkMode"
              @change="commomStore.toggleDarkMode()"
            />
          </div>
        </div>

        <!-- ===== Account ===== -->
        <div v-show="activeTab === 'account'" class="settings-list">
          <div class="settings-row">
            <div class="settings-row__text">
              <div class="settings-row__title">{{ t('header.displayName') }}</div>
              <div class="settings-row__desc">{{ t('header.displayNameDesc') }}</div>
            </div>
            <div class="settings-row__value">
              <span class="settings-row__current">{{ user.name }}</span>
              <el-button @click="openDisplayNameDialog">{{ t('header.modify') }}</el-button>
            </div>
          </div>
          <div class="settings-row">
            <div class="settings-row__text">
              <div class="settings-row__title">
                {{ initPwd ? t('header.setPassword') : t('header.changePassword') }}
              </div>
              <div class="settings-row__desc">{{ t('header.changePasswordDesc') }}</div>
            </div>
            <el-button @click="emit('open-password')">{{ t('header.modify') }}</el-button>
          </div>
          <div class="settings-row" v-if="user.tenant?.tenantType === 'PERSONAL'">
            <div class="settings-row__text">
              <div class="settings-row__title">{{ t('header.avatarSettings') }}</div>
              <div class="settings-row__desc">{{ t('header.avatarSettingsDesc') }}</div>
            </div>
            <el-button @click="emit('open-avatar')">{{ t('header.modify') }}</el-button>
          </div>
        </div>

        <!-- ===== Privacy (SYS users only) ===== -->
        <div v-show="activeTab === 'privacy'" class="settings-list">
          <div class="settings-row" v-if="user.userType === 'SYS'">
            <div class="settings-row__text">
              <div class="settings-row__title">{{ t('header.dataMasking') }}</div>
              <div class="settings-row__desc">{{ t('header.dataMaskingDesc') }}</div>
            </div>
            <el-switch
              :model-value="commomStore.masking"
              @change="commomStore.toggleMasking()"
            />
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button type="primary" @click="visible = false">{{ t('common.close') }}</el-button>
      </div>
    </template>
  </el-dialog>

  <!-- Display name edit — small modal stacked above the settings panel. -->
  <el-dialog
    v-model="displayNameDialogVisible"
    :title="t('header.editDisplayName')"
    width="420"
    append-to-body
    :close-on-click-modal="false"
    @closed="displayNameSaving = false"
  >
    <el-form
      ref="displayNameFormRef"
      :model="displayNameForm"
      :rules="displayNameRules"
      label-width="0"
    >
      <el-form-item prop="nickname">
        <el-input
          v-model="displayNameForm.nickname"
          maxlength="120"
          show-word-limit
          :placeholder="t('header.displayNamePlaceholder')"
          @keyup.enter="handleDisplayNameSubmit"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="displayNameDialogVisible = false">{{ t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="displayNameSaving" @click="handleDisplayNameSubmit">
          {{ t('common.confirm') }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { useCommonStore, useUserStore, type FontTheme } from "@/store/common"
import { setUserConfig } from "@/api/common"
import { ElMessage } from "element-plus"
import { User, Brush, Lock } from "@element-plus/icons-vue"
import { useI18n } from "vue-i18n"
import {
  getCurrentLocale,
  setCurrentLocale,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from "@/i18n"

const { t } = useI18n()

const commomStore = useCommonStore()
const userStore = useUserStore()
const user = computed(() => userStore.userInfo)

const visible = defineModel<boolean>("visible", { default: false })
defineProps<{ initPwd: boolean }>()
const emit = defineEmits<{
  (e: "open-password"): void
  (e: "open-avatar"): void
}>()

type SettingsTab = "account" | "appearance" | "privacy"
const activeTab = ref<SettingsTab>("appearance")
const handleTabSelect = (index: string) => {
  activeTab.value = index as SettingsTab
}

// Always land on the first tab when the dialog reopens.
watch(visible, (open) => {
  if (open) activeTab.value = "appearance"
})

const currentLocale = computed<SupportedLocale>(() => getCurrentLocale())
const handleLocaleChange = (code: string) => {
  if ((SUPPORTED_LOCALES as readonly string[]).includes(code)) {
    setCurrentLocale(code as SupportedLocale, true)
  }
}

const fontThemeOptions: { value: FontTheme; labelKey: string }[] = [
  { value: "modern", labelKey: "header.fontThemeModern" },
  { value: "editorial", labelKey: "header.fontThemeEditorial" },
  { value: "friendly", labelKey: "header.fontThemeFriendly" },
]

// ---- Display name (nickname) editing ----
const displayNameDialogVisible = ref(false)
const displayNameSaving = ref(false)
const displayNameFormRef = ref<any>(null)
const displayNameForm = reactive({ nickname: "" })
const displayNameRules = reactive({
  nickname: [
    { required: true, message: t('header.displayNameRequired'), trigger: 'blur' },
    { min: 1, max: 120, message: t('header.displayNameLength'), trigger: 'blur' },
  ],
})

const openDisplayNameDialog = () => {
  // The login `username` is immutable; we edit the display name (nickname).
  // It isn't stored separately on the client, so seed from the rendered name.
  displayNameForm.nickname = userStore.userInfo.name || ""
  displayNameDialogVisible.value = true
}

const handleDisplayNameSubmit = () => {
  if (!displayNameFormRef.value) return
  displayNameFormRef.value.validate((valid: boolean) => {
    if (!valid) return
    const nickname = displayNameForm.nickname.trim()
    if (!nickname) return
    displayNameSaving.value = true
    // Persist via the same PUT /sys-user endpoint the avatar flow uses, but
    // call setUserConfig directly (not userStore.setInfo) so we get a real
    // loading/error lifecycle — setInfo swallows errors and never resolves.
    setUserConfig({ ...userStore.userInfo, nickname })
      .then(() => {
        userStore.setFlag(false)
        userStore.getInfo()
        ElMessage.success(t('header.displayNameUpdated'))
        displayNameDialogVisible.value = false
      })
      .catch(() => {})
      .finally(() => {
        displayNameSaving.value = false
      })
  })
}
</script>

<style>
.settings-dialog .settings-shell {
  display: flex;
  min-height: 320px;
}
.settings-dialog .settings-nav {
  width: 168px;
  flex: 0 0 168px;
  --el-menu-bg-color: transparent;
}
.settings-dialog .settings-nav.el-menu {
  border-right: 1px solid var(--el-border-color-lighter, #ececec);
}
.settings-dialog .settings-nav .el-menu-item {
  height: 44px;
  line-height: 44px;
}
.settings-dialog .settings-content {
  flex: 1 1 auto;
  min-width: 0;
  padding-left: 20px;
}

.settings-dialog .settings-list {
  display: flex;
  flex-direction: column;
}
.settings-dialog .settings-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 4px;
  border-bottom: 1px solid var(--el-border-color-lighter, #ececec);
}
.settings-dialog .settings-row:last-child {
  border-bottom: none;
}
.settings-dialog .settings-row__text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-right: 16px;
}
.settings-dialog .settings-row__title {
  font-size: 14px;
  font-weight: 500;
  color: var(--el-text-color-primary, #303133);
  line-height: 20px;
}
.settings-dialog .settings-row__desc {
  font-size: 12px;
  color: var(--el-text-color-secondary, #909399);
  line-height: 18px;
}
.settings-dialog .settings-row__beta {
  margin-left: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: var(--el-color-warning, #e6a23c);
  vertical-align: middle;
}
.settings-dialog .settings-row__value {
  display: flex;
  align-items: center;
  gap: 12px;
}
.settings-dialog .settings-row__current {
  color: var(--el-text-color-regular, #606266);
  font-size: 13px;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ---- Font theme picker ----
   Stacked layout: title row on top, three preview cards in a
   row below. Each card renders sample English + Chinese using
   the font stack of that theme so the user can compare
   visually before committing. Active card is teal-bordered. */
.settings-dialog .settings-row--stacked {
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
}
.settings-dialog .font-theme-picker {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.settings-dialog .font-theme-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 12px 10px;
  border: 1px solid var(--el-border-color-lighter, #e4e7ed);
  border-radius: 8px;
  background-color: var(--el-fill-color-blank, #fff);
  cursor: pointer;
  font: inherit;
  text-align: left;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease;
}
.settings-dialog .font-theme-card:hover {
  border-color: var(--el-color-primary, #26C1BD);
  transform: translateY(-1px);
}
.settings-dialog .font-theme-card.is-active {
  border-color: var(--el-color-primary, #26C1BD);
  box-shadow: 0 0 0 2px rgba(38, 193, 189, 0.18);
}
/* Per-card font preview — each card forces its own stack so
   the sample text actually looks different across the three
   options. Mirrors the theme definitions in style.scss. */
.settings-dialog .font-theme-card--modern {
  font-family:
    'Inter', -apple-system, BlinkMacSystemFont,
    'Segoe UI Variable', 'Segoe UI', system-ui,
    'PingFang SC', 'PingFang HK',
    'Microsoft YaHei UI', 'Microsoft YaHei',
    'Noto Sans SC', 'Source Han Sans SC',
    Roboto, sans-serif;
}
.settings-dialog .font-theme-card--editorial {
  font-family:
    'Source Sans 3', 'Source Sans Pro', -apple-system,
    BlinkMacSystemFont, 'Segoe UI', system-ui,
    'PingFang SC', 'Microsoft YaHei UI', 'Microsoft YaHei',
    'Source Han Sans SC', 'Noto Sans SC',
    sans-serif;
}
.settings-dialog .font-theme-card--friendly {
  font-family:
    'Manrope', -apple-system, BlinkMacSystemFont,
    'Segoe UI Variable', 'Segoe UI', system-ui,
    'PingFang SC',
    'Microsoft YaHei UI', 'Microsoft YaHei',
    'Noto Sans SC', sans-serif;
}
.settings-dialog .font-theme-card__sample-en {
  font-size: 16px;
  font-weight: 600;
  color: var(--el-text-color-primary, #303133);
  line-height: 1.2;
}
.settings-dialog .font-theme-card__sample-zh {
  font-size: 14px;
  font-weight: 500;
  color: var(--el-text-color-regular, #606266);
  line-height: 1.3;
}
.settings-dialog .font-theme-card__label {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 500;
  color: var(--el-text-color-secondary, #909399);
  letter-spacing: 0.2px;
}
.settings-dialog .font-theme-card.is-active .font-theme-card__label {
  color: var(--el-color-primary, #26C1BD);
}

/* Dark-mode tweaks — html.dark-scoped to keep light mode identical
   (per dark-mode BETA invariant). */
html.dark .settings-dialog .settings-nav.el-menu {
  border-right-color: var(--imles-border, #3a3a3a);
}
html.dark .settings-dialog .font-theme-card {
  background-color: var(--imles-surface-2, #2a2a2a);
  border-color: var(--imles-border, #3a3a3a);
}
html.dark .settings-dialog .font-theme-card:hover {
  border-color: var(--imles-accent, #3dd8d1);
}
html.dark .settings-dialog .font-theme-card.is-active {
  border-color: var(--imles-accent, #3dd8d1);
  box-shadow: 0 0 0 2px rgba(61, 216, 209, 0.22);
}
html.dark .settings-dialog .font-theme-card__sample-en {
  color: var(--imles-text-primary, #f5f5f5);
}
html.dark .settings-dialog .font-theme-card__sample-zh {
  color: var(--imles-text-secondary, #c8c8c8);
}
html.dark .settings-dialog .font-theme-card.is-active .font-theme-card__label {
  color: var(--imles-accent, #3dd8d1);
}
</style>
